import React, { useCallback, useMemo } from 'react';
import { Select, SelectProps, Skeleton } from 'antd';
import { isNumber } from 'lodash';
import debounce from 'lodash/debounce';

// From Ant Design Select component
const DEFAULT_OPTION_HEIGHT = 32;
const DEFAULT_THRESHOLD_HEIGHT = 100;
const DEFAULT_SCROLL_DEBOUNCE = 200;

export interface InfiniteScrollSelectProps<ValueType = unknown>
  extends Omit<SelectProps<ValueType>, 'onPopupScroll'> {
  loadMore: () => void;
  hasMore: boolean;
  threshold?: number;
  scrollDebounce?: number;
  totalLoaded?: number;
  itemHeight?: number;
}

const InfiniteScrollSelect = <ValueType extends unknown>({
  loadMore,
  hasMore,
  loading,
  totalLoaded,
  dropdownRender,
  threshold = DEFAULT_THRESHOLD_HEIGHT,
  scrollDebounce = DEFAULT_SCROLL_DEBOUNCE,
  itemHeight = DEFAULT_OPTION_HEIGHT,
  ...selectProps
}: InfiniteScrollSelectProps<ValueType>): React.ReactElement => {
  const handleLoadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    loadMore();
  }, [loading, hasMore, loadMore]);

  const handleScroll = useMemo(
    () =>
      debounce((e: React.UIEvent<HTMLDivElement>) => {
        const { target } = e;
        if (!(target instanceof HTMLDivElement)) return;

        const { scrollTop, clientHeight, scrollHeight } = target;

        if (loading || !hasMore) return;

        // Check if we've scrolled close to the bottom (with the specified threshold)
        if (scrollHeight - scrollTop - clientHeight < threshold) handleLoadMore();
      }, scrollDebounce),
    [handleLoadMore, scrollDebounce, threshold],
  );

  // Calculate list height based on total items and hasMore status
  const getListHeight = () => {
    if (!isNumber(totalLoaded) || !hasMore) return;

    // Only apply custom height if totalLoaded is defined and there are more items to load
    // Custom height is used to allow loading more items when the number of items didn't reach
    // the default height of the dropdown
    const calculatedHeight = (totalLoaded - 1) * itemHeight;
    return Math.min(Math.max(calculatedHeight, 100), 256);
  };

  // Renders
  const renderSkeleton = () => {
    return <Skeleton.Input size="small" block active />;
  };

  const renderDropdown = useCallback(
    (menu: React.ReactNode) => {
      const customContent = (
        <div>
          {menu}
          {loading && renderSkeleton()}
        </div>
      );

      // If custom dropdownRender provided, chain them
      return dropdownRender ? dropdownRender(customContent) : customContent;
    },
    [loading, dropdownRender],
  );

  return (
    <Select
      {...selectProps}
      listHeight={getListHeight()}
      loading={loading}
      notFoundContent={loading ? renderSkeleton() : undefined}
      onPopupScroll={handleScroll}
      filterOption={false}
      dropdownRender={renderDropdown}
    />
  );
};

export default InfiniteScrollSelect;
